// src/pages/Permissions/PermissionList.jsx
// -----------------------------------------------------------------------------
// Admin screen listing and managing fine-grained role permissions.
// Performs full CRUD (Read, Create, Update, Delete) via permissionService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import permissionService from '../../api/permissionService';
import roleService from '../../api/roleService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function PermissionList() {
  const [roles, setRoles] = useState([]);
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('roleID');

  const [selectedRoleFilter, setSelectedRoleFilter] = useState(roleParam || '');

  // Sync role filter if URL parameters change
  useEffect(() => {
    if (roleParam) {
      setSelectedRoleFilter(roleParam);
    }
  }, [roleParam]);

  // Fetch all roles to populate dropdown selection in form & filter
  useEffect(() => {
    roleService
      .getAll()
      .then((res) => setRoles(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data, // Already filtered by searchTerm and sorted
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(permissionService, {
    idKey: 'permissionID',
    itemName: 'Permission',
    initialSort: { key: 'permissionID', direction: 'asc' },
  });

  // Local pagination states (to properly support custom Role filter)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Apply Role ID filter to the searched and sorted data
  const filteredByRole = useMemo(() => {
    if (!selectedRoleFilter) return data;
    return data.filter((item) => Number(item.roleID) === Number(selectedRoleFilter));
  }, [data, selectedRoleFilter]);

  // Slice for current page
  const paginatedPermissions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredByRole.slice(startIndex, startIndex + pageSize);
  }, [filteredByRole, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredByRole.length / pageSize) || 1;
  const totalRecords = filteredByRole.length;

  // Reset pagination if search or role filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRoleFilter]);

  // Schema configuration for Permission_TBL
  const permissionFields = useMemo(() => [
    {
      name: 'permissionID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'roleID',
      label: 'Role',
      type: 'select',
      required: true,
      options: roles.map((r) => ({ label: r.roleName, value: r.roleID })),
    },
    {
      name: 'permissionKey',
      label: 'Permission Key',
      type: 'varchar',
      required: true,
      maxLength: 100,
      placeholder: 'e.g. VIEW_FULL_STATUS_HISTORY, VIEW_INTERNAL_COMMENTS',
    },
    {
      name: 'permissionValue',
      label: 'Permission Value',
      type: 'checkbox',
      required: false,
      placeholder: 'Grant this permission override',
      defaultValue: false,
    },
  ], [roles]);

  const columns = [
    { key: 'permissionID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'roleID',
      label: 'Role Name',
      sortable: true,
      render: (row) => {
        const found = roles.find((r) => Number(r.roleID) === Number(row.roleID));
        return found ? found.roleName : `Role ID: ${row.roleID}`;
      },
    },
    { key: 'permissionKey', label: 'Permission Key', sortable: true },
    {
      key: 'permissionValue',
      label: 'Value (Allowed)',
      sortable: true,
      render: (row) => row.permissionValue ? (
        <span style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
          ✓ Allowed
        </span>
      ) : (
        <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
          ✕ Denied
        </span>
      ),
    },
  ];

  const renderHeaderActions = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="roleFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Filter by Role:
        </label>
        <select
          id="roleFilterSelect"
          value={selectedRoleFilter}
          onChange={(e) => setSelectedRoleFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '150px'
          }}
        >
          <option value="">All Roles</option>
          {roles.map((r) => (
            <option key={r.roleID} value={r.roleID}>
              {r.roleName}
            </option>
          ))}
        </select>
      </div>
      {selectedRoleFilter && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedRoleFilter('')}
          style={{ padding: '6px 12px', fontSize: '12px' }}
        >
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Permissions Configuration</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Configure fine-grained permission overrides per Role beyond the four base roles.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Permission
        </button>
      </div>

      {/* Main Grid Table List */}
      <TableList
        columns={columns}
        data={filteredByRole}
        paginatedData={paginatedPermissions}
        loading={loading}
        idKey="permissionID"
        emptyMessage="No permissions overrides configured."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        renderHeaderActions={renderHeaderActions}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={permissionFields}
          initialValues={selectedRecord || { roleID: '', permissionKey: '', permissionValue: false }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Permission"
        />
      )}

      {/* Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Permission"
        itemLabel={selectedRecord ? `permission override '${selectedRecord.permissionKey}'` : 'Permission'}
        deleting={deleting}
      />
    </div>
  );
}
